import { formatNumber } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, Output, SimpleChanges } from "@angular/core";
import { ChartConfiguration, Plugin } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { FONT_FAMILY, GRID, INK, SLATE, fitLabel } from "../../charts/bar-chart/bar-chart.component";
import { getInsights, Linqset } from "../../common/linqset-model";
import { NodeTypeData } from "../node-types/node-type-data";
import { CapacityRow, CapacityStatus, capacityRows } from "./node-chart-data";
import { defaultCapacityHours } from "../../common/organisation";
import { capacityIsKnown } from "../../model-services/role-capacity";

// the status colours, kept for how loaded a node is: each also has its label in the legend and its percentage on the bar
const STATUS_COLOURS: Record<CapacityStatus, string> = { over: '#D8443B', near: '#E8A400', under: '#8a93a6' };
const STATUS_LABELS: Record<CapacityStatus, string> = { over: 'Over capacity', near: '85% to 100%', under: 'Room to spare' };

// a dashed line at 100% of capacity, across the bars
const capacityLine: Plugin = {
    id: 'capacityLine',
    afterDatasetsDraw(chart) {
        const x = (chart.scales as any).x?.getPixelForValue(100);
        const area = chart.chartArea;
        if (x === undefined || x < area.left || x > area.right) return;
        const ctx = chart.ctx;
        ctx.save();
        ctx.strokeStyle = INK;
        ctx.lineWidth = 1.5;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(x, area.top);
        ctx.lineTo(x, area.bottom);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = INK;
        ctx.font = `600 11px ${FONT_FAMILY}`;
        ctx.textAlign = 'center';
        ctx.fillText('Capacity', x, area.top - 6);
        ctx.restore();
    },
};

// how loaded each role or person is: its work in a year against its capacity, the most loaded first, with a line at 100%
@Component({
    selector: 'app-node-capacity-chart',
    templateUrl: './node-capacity.html',
    styleUrls: ['./node-charts.css'],
    standalone: false
})
export class NodeCapacityComponent implements OnChanges {
    @Input() linqset: Linqset
    @Input() typeData: NodeTypeData
    @Input() trigger: any
    @Input() topNumber: number

    @Output() selectNodeEvent = new EventEmitter<string>();

    rows: CapacityRow[] = []
    shown: CapacityRow[] = []
    noCapacity = 0
    counts: { status: CapacityStatus, label: string, colour: string, count: number }[] = []
    datasets: any[] = []
    labels: string[] = []
    options: ChartConfiguration['options'] = {}
    plugins = [ChartDataLabels, capacityLine]

    constructor(@Inject(LOCALE_ID) private locale: string, private changeDetectorRef: ChangeDetectorRef) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['trigger'] || changes['typeData'] || changes['linqset'] || changes['topNumber']) this.populate();
    }

    get height() {
        return Math.max(220, Math.min(1100, this.shown.length * 26 + 100));
    }

    private populate() {
        if (!this.typeData || !this.linqset) return;
        // a role given just enough capacity for its work, as an external organisation, or one with no capacity of its own
        // when the organisation has no default, would read 100%: it's counted among those with no capacity set
        const defaultHours = defaultCapacityHours(this.linqset.organisation);
        const result = capacityRows((this.typeData.list || []).filter(n => !n.excluded).map(n => {
            const insights = getInsights(n, this.linqset);
            const known = capacityIsKnown(n, insights, defaultHours);
            return { key: n._key, name: n.name || 'Missing name', hours: insights?.hours || 0, capacity: known ? insights?.modelledCapacity || 0 : 0 };
        }));
        this.rows = result.rows;
        this.noCapacity = result.noCapacity;
        this.shown = this.rows.slice(0, this.topNumber || 35);
        this.counts = (['over', 'near', 'under'] as CapacityStatus[]).map(status => ({ status, label: STATUS_LABELS[status], colour: STATUS_COLOURS[status], count: this.rows.filter(r => r.status === status).length }));

        this.labels = this.shown.map(r => r.name);
        this.datasets = [{
            label: 'Work against capacity',
            data: this.shown.map(r => Math.round(r.utilisation * 1000) / 10),
            backgroundColor: this.shown.map(r => STATUS_COLOURS[r.status]),
            hoverBackgroundColor: this.shown.map(r => STATUS_COLOURS[r.status]),
            borderRadius: 4, borderSkipped: false, maxBarThickness: 22,
        }];
        const most = Math.max(100, ...this.shown.map(r => r.utilisation * 100));
        this.options = this.buildOptions(most);
        this.changeDetectorRef.markForCheck();
    }

    private hours(v: number) {
        return formatNumber(Math.round(v), this.locale, '1.0-0');
    }

    private buildOptions(most: number): ChartConfiguration['options'] {
        return <any>{
            responsive: true,
            maintainAspectRatio: false,
            indexAxis: 'y',
            layout: { padding: { top: 18, right: 48 } },
            scales: {
                x: {
                    beginAtZero: true, suggestedMax: Math.ceil(most * 1.08 / 10) * 10,
                    grid: { color: GRID, drawTicks: false }, border: { display: false },
                    ticks: { color: SLATE, padding: 6, font: { family: FONT_FAMILY, size: 11 }, callback: (v: any) => `${v}%` },
                    title: { display: true, text: 'Work in a year, as a share of capacity', color: SLATE, font: { family: FONT_FAMILY, size: 12, weight: 600 }, padding: { top: 8 } },
                },
                y: {
                    grid: { display: false }, border: { color: '#d5d9e2' },
                    ticks: {
                        color: INK, autoSkip: false, padding: 6, font: { family: FONT_FAMILY, size: 12 },
                        callback: function (this: any, v: any) { return fitLabel(this.chart, String(this.getLabelForValue(Number(v)) ?? '')); },
                    },
                },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: INK, padding: 10, cornerRadius: 6,
                    titleFont: { family: FONT_FAMILY, size: 12, weight: 600 }, bodyFont: { family: FONT_FAMILY, size: 12 },
                    callbacks: {
                        label: (c: any) => {
                            const r = this.shown[c.dataIndex];
                            return r ? `${this.hours(r.hours)} of ${this.hours(r.capacity)} hours a year (${Math.round(r.utilisation * 100)}%)` : '';
                        },
                        afterLabel: (c: any) => {
                            const r = this.shown[c.dataIndex];
                            if (!r) return '';
                            return r.status === 'over' ? `${this.hours(r.hours - r.capacity)} hours more than it can do` : `${this.hours(r.capacity - r.hours)} hours to spare`;
                        },
                    },
                },
                datalabels: {
                    color: INK, anchor: 'end', align: 'end', offset: 4, clamp: true, clip: false, display: true,
                    font: { family: FONT_FAMILY, size: 11, weight: 600 },
                    formatter: (v: number) => `${Math.round(v)}%`,
                },
            },
        };
    }

    onClick(event: any) {
        const index = event?.active?.[0]?.index;
        if (index !== undefined && this.shown[index]) this.selectNodeEvent.emit(this.shown[index].key);
    }
}
